// CopyPOAModal.tsx
// Fetches an existing POA, lets the user choose which sections to copy,
// then seeds sessionStorage and navigates to /poa/new.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Copy } from 'lucide-react';
import { getPOADetail } from '../../services/poa';
import type { POADetail, POATaxMatter, POAFormState } from '../../types/poa';
import {
  MATTER_DESCRIPTIONS, LICENSING_JURISDICTIONS,
  DEFAULT_CIVIL_PENALTY_COVERAGE, CIVIL_PENALTY_DESCRIPTION, CIVIL_PENALTY_FORM_NUMBER,
} from './poaConstants';

/** Case-insensitive lookup — returns the correctly-cased option value if found, otherwise the raw value. */
function matchOption<T extends string>(value: string | null | undefined, options: readonly T[]): string {
  if (!value) return '';
  const upper = value.toUpperCase();
  return options.find((o) => o.toUpperCase() === upper) ?? value;
}

const SESSION_KEY = 'poa-new-draft';

interface Section {
  key:   'taxpayer' | 'representatives' | 'taxMatters' | 'acts';
  label: string;
  desc:  string;
}

const SECTIONS: Section[] = [
  { key: 'taxpayer',        label: 'Taxpayer Information', desc: 'Name, address, EIN, phone' },
  { key: 'representatives', label: 'Representatives',      desc: 'All rep rows (signatures cleared)' },
  { key: 'taxMatters',      label: 'Tax Matters',          desc: 'All matter / form / period rows' },
  { key: 'acts',            label: 'Acts & Limits',        desc: 'Lines 4–6 checkboxes and text' },
];

interface Props {
  poaId:        string;
  taxpayerName: string;
  onClose:      () => void;
}

export default function CopyPOAModal({ poaId, taxpayerName, onClose }: Props) {
  const navigate = useNavigate();

  const [poa,      setPoa]      = useState<POADetail | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<Section['key']>>(
    new Set(['taxpayer', 'representatives', 'taxMatters', 'acts'])
  );

  useEffect(() => {
    getPOADetail(poaId)
      .then(setPoa)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [poaId]);

  function toggle(key: Section['key']) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function handleCopy() {
    if (!poa) return;

    // Build a blank base state matching INITIAL_STATE shape
    const blank: POAFormState = {
      peClientCode: '',
      taxpayerName: '', taxpayerAddress: '', taxpayerEin: '',
      taxpayerSsn: '', taxpayerTinType: '', taxpayerPhone: '', taxpayerEmail: '', taxpayerPlanNumber: '',
      representatives: [{
        id: crypto.randomUUID(), repOrder: 1,
        name: '', address: '', cafNumber: '', ptin: '',
        phone: '', fax: '',
        receivesNotices: true, isNewAddress: false, isNewPhone: false, isNewFax: false,
        designationCode: 'b', licensingJurisdiction: 'Alabama', licenseNumber: '',
      }],
      taxMatters: [
        {
          id: crypto.randomUUID(), matterDescription: '', taxFormNumber: '',
          periodType: 'year', periodStart: new Date().getFullYear(), periodEnd: '', periodRaw: '',
        },
        ...(DEFAULT_CIVIL_PENALTY_COVERAGE ? [{
          id: crypto.randomUUID(),
          matterDescription: CIVIL_PENALTY_DESCRIPTION,
          taxFormNumber:     CIVIL_PENALTY_FORM_NUMBER,
          periodType:        'year' as const,
          periodStart:       new Date().getFullYear(),
          periodEnd:         '' as number | '',
          periodRaw:         '',
          autoMirror:        true,
        }] : []),
      ],
      isSpecificUseNotOnCaf: false,
      authIntermediateServiceProvider: false, authDisclosureThirdParties: false,
      authSubstituteAddRep: false, authSignReturn: false,
      authOtherActs: '', unauthOtherDeletions: '', retainPriorPoa: false,
    };

    const state: POAFormState = { ...blank };

    if (selected.has('taxpayer')) {
      state.peClientCode        = poa.peClientCode        ?? '';
      state.taxpayerName        = poa.taxpayerName        ?? '';
      state.taxpayerAddress     = poa.taxpayerAddress     ?? '';
      state.taxpayerEin         = poa.taxpayerEin         ?? '';
      state.taxpayerPhone       = poa.taxpayerPhone       ?? '';
      state.taxpayerEmail       = poa.taxpayerEmail       ?? '';
      state.taxpayerPlanNumber  = poa.taxpayerPlanNumber  ?? '';
      // SSN intentionally not copied — it's masked in the detail response
    }

    if (selected.has('representatives')) {
      state.representatives = poa.representatives.map((r, i) => ({
        id:                   crypto.randomUUID(),
        repOrder:             i + 1,
        name:                 r.repName,
        address:              r.repAddress,
        cafNumber:            r.cafNumber ?? '',
        ptin:                 r.ptin      ?? '',
        phone:                r.phone     ?? '',
        fax:                  r.fax       ?? '',
        receivesNotices:      r.receivesNotices,
        isNewAddress:         false,
        isNewPhone:           false,
        isNewFax:             false,
        designationCode:      r.designationCode,
        licensingJurisdiction: matchOption(r.licensingJurisdiction, LICENSING_JURISDICTIONS),
        licenseNumber:        r.licenseNumber          ?? '',
        peStaffUser:          r.peStaffUser            ?? undefined,
      }));
    }

    if (selected.has('taxMatters')) {
      state.taxMatters = poa.taxMatters.map((m) => ({
        id:                crypto.randomUUID(),
        matterDescription: matchOption(m.matterDescription, MATTER_DESCRIPTIONS),
        taxFormNumber:     m.taxFormNumber,
        periodType:        (m.periodType ?? 'year') as POATaxMatter['periodType'],
        periodStart:       m.periodStart ?? new Date().getFullYear(),
        periodEnd:         (m.periodEnd ?? '') as number | '',
        periodRaw:         m.periodRaw   ?? '',
      }));
    }

    if (selected.has('acts')) {
      state.isSpecificUseNotOnCaf          = poa.isSpecificUseNotOnCaf;
      state.authIntermediateServiceProvider = poa.authIntermediateServiceProvider;
      state.authDisclosureThirdParties      = poa.authDisclosureThirdParties;
      state.authSubstituteAddRep            = poa.authSubstituteAddRep;
      state.authSignReturn                  = poa.authSignReturn;
      state.authOtherActs                   = poa.authOtherActs         ?? '';
      state.unauthOtherDeletions            = poa.unauthOtherDeletions  ?? '';
      state.retainPriorPoa                  = poa.retainPriorPoa;
    }

    // Seed sessionStorage so usePOAForm picks it up on mount
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(state)); } catch { /* ignore */ }

    navigate('/poa/new');
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  };
  const modal: React.CSSProperties = {
    background: 'var(--surface, #1e2030)',
    border: '1px solid var(--border)', borderRadius: 12, padding: 24,
    width: 480, maxWidth: '95vw',
    boxShadow: '0 8px 40px rgba(0,0,0,.6)',
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Copy to New POA</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
              Copying from: <strong>{taxpayerName}</strong>
            </div>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        {loading && <div style={{ fontSize: 13, color: 'var(--muted)', padding: '16px 0' }}>Loading…</div>}
        {error   && <div style={{ fontSize: 13, color: 'var(--bad)',   padding: '16px 0' }}>{error}</div>}

        {poa && (
          <>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
              Select which sections to pre-fill. You can edit any field before submitting.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 20 }}>
              {SECTIONS.map((s) => (
                <label
                  key={s.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    background: selected.has(s.key) ? 'rgba(122,162,255,.08)' : 'rgba(255,255,255,.02)',
                    border: `1px solid ${selected.has(s.key) ? 'rgba(122,162,255,.3)' : 'transparent'}`,
                    transition: 'all .15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(s.key)}
                    onChange={() => toggle(s.key)}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{s.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {selected.has('taxpayer') && (
              <div style={{
                fontSize: 11, color: 'var(--muted)', marginBottom: 16,
                background: 'rgba(255,255,255,.03)', borderRadius: 6, padding: '6px 10px',
              }}>
                Note: SSN is not copied for security. Re-enter it on the Taxpayer step if needed.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={onClose} style={{ fontSize: 14 }}>Cancel</button>
              <button
                className="btn"
                onClick={handleCopy}
                disabled={selected.size === 0}
                style={{
                  fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                  background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
                  color: 'var(--accent)', opacity: selected.size === 0 ? .45 : 1,
                }}
              >
                <Copy size={13} /> Copy & Open New Form
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
